import React, { useCallback, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import U2000SaveCancelDialog from '../../../u2000Core/components/dialog/U2000SaveCancelDialog';
import U2000InputText from '../../../u2000Core/components/formControls/input/U2000InputText';
import U2000InputMemo from '../../../u2000Core/components/formControls/input/U2000InputMemo';
import U2000InputDate from '../../../u2000Core/components/formControls/input/U2000InputDate';
import U2000Label from '../../../u2000Core/components/formControls/label/U2000Label';
import u2000FormState, {
  U2000FormStateEnumEditMode,
  U2000FormStateEnumState,
  type U2000FormState,
} from '../../../u2000Core/reducer/formReducer/U2000FormState';
import { u2000IntFormState, useU2000FormStateHandlers } from '../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { NS } from '../../U3301';
import type { U3301EventDeleteScope, U3301EventSearchRow } from '../../U3301Service';
import { u3301IsHexColor } from '../../utils/u3301AgendaDateRange';

type EventFormRow = {
  title: string;
  description: string;
  eventDate: string;
};

interface U3301EventDialogProps {
  event: U3301EventSearchRow;
  canEdit: boolean;
  canDelete: boolean;
  onClose: () => void;
  onSaveManual: (payload: { title: string; description: string; eventDate: string }) => Promise<void>;
  onMove: (eventDate: string) => Promise<void>;
  onDelete: (scope: U3301EventDeleteScope) => Promise<void>;
}

const U3301EventDialog: React.FC<U3301EventDialogProps> = ({
  event,
  canEdit,
  canDelete,
  onClose,
  onSaveManual,
  onMove,
  onDelete,
}) => {
  const { t } = useTranslation();
  const isManual = event.origin === 'MANUAL';
  const editable = canEdit && event.category.editableInCalendar;
  const hasSeries = Boolean(event.seriesId);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [formState, formStateDispatch] = useReducer(
    u2000FormState,
    event,
    (ev): U2000FormState<EventFormRow> => ({
      ...u2000IntFormState,
      state: editable ? U2000FormStateEnumState.EDIT : U2000FormStateEnumState.READ,
      editMode: U2000FormStateEnumEditMode.EDIT,
      current: {
        title: ev.title,
        description: ev.description ?? '',
        eventDate: ev.eventDate,
      },
      old: {
        title: ev.title,
        description: ev.description ?? '',
        eventDate: ev.eventDate,
      },
    }),
  );

  const formStateHandlers = useU2000FormStateHandlers<EventFormRow>({
    formState,
    formStateDispatch,
  });

  const row = formStateHandlers.getData();
  const dateChanged = row.eventDate !== event.eventDate;
  const fieldsChanged =
    row.title !== event.title || row.description !== (event.description ?? '') || dateChanged;

  const isValid =
    row.eventDate.trim() !== '' &&
    (!isManual || row.title.trim() !== '') &&
    (isManual ? fieldsChanged : dateChanged);

  const handleClose = async (action: 'save' | 'cancel') => {
    if (action === 'cancel' || !editable) {
      onClose();
      return;
    }
    if (!isValid || saving || deleting) return;
    setSaving(true);
    try {
      if (isManual) {
        await onSaveManual({
          title: row.title,
          description: row.description,
          eventDate: row.eventDate,
        });
      } else if (dateChanged) {
        await onMove(row.eventDate);
      } else {
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (scope: U3301EventDeleteScope) => {
    if (!canDelete || !isManual || deleting || saving) return;
    setDeleting(true);
    try {
      await onDelete(scope);
    } finally {
      setDeleting(false);
    }
  };

  const handleEventDateChange = useCallback(
    (eventDate: string) => {
      const current = formStateHandlers.getData();
      if (current.eventDate === eventDate) return;
      formStateHandlers.setData({ ...current, eventDate });
    },
    [formStateHandlers],
  );

  const titleReadonly = !editable || !isManual;
  const dateDisabled = !editable;
  const showDelete = canDelete && isManual && event.category.editableInCalendar;

  return (
    <U2000SaveCancelDialog
      isOpen
      onClose={handleClose}
      title={event.title}
      isValid={isValid && !saving && !deleting}
      readOnly={!editable}
      width="medium"
      contentOverflowVisible
      footerStartSlot={
        showDelete ? (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-300 bg-white px-4 py-2.5 text-sm font-medium text-red-700 shadow-xs transition-all duration-200 hover:bg-red-50 focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={saving || deleting}
              onClick={() => void handleDelete('DAY')}
            >
              {hasSeries ? t('btnDeleteDay', { ns: NS }) : t('btnDelete', { ns: NS })}
            </button>
            {hasSeries && (
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-500 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-800 shadow-xs transition-all duration-200 hover:bg-red-100 focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={saving || deleting}
                onClick={() => void handleDelete('SERIES')}
              >
                {t('btnDeleteSeries', { ns: NS })}
              </button>
            )}
          </div>
        ) : undefined
      }
    >
      <div className="space-y-3 p-1">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span
            className="inline-block h-3 w-3 rounded-full"
            style={{
              backgroundColor: u3301IsHexColor(event.category.effectiveColorCode)
                ? event.category.effectiveColorCode
                : '#64748b',
            }}
          />
          {event.category.label}
          <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-xs">
            {t(`valEventOrigin${event.origin}`, { ns: NS })}
          </span>
        </div>

        {!isManual && <p className="text-sm text-amber-700">{t('lblLinkedEventReadOnly', { ns: NS })}</p>}
        {hasSeries && isManual && (
          <p className="text-sm text-slate-600">{t('msgSeriesDeleteHint', { ns: NS })}</p>
        )}

        <U2000InputText
          formStateHandlers={formStateHandlers}
          fieldName="title"
          label={t('lblEventTitle', { ns: NS })}
          readonly={titleReadonly}
          required={isManual}
        />

        <U2000InputMemo
          formStateHandlers={formStateHandlers}
          fieldName="description"
          label={t('lblEventDescription', { ns: NS })}
          readonly={titleReadonly}
          rows={3}
        />

        <div>
          <U2000Label label={t('lblEventDate', { ns: NS })} required />
          <U2000InputDate
            value={row.eventDate}
            onChange={handleEventDateChange}
            disabled={dateDisabled}
          />
        </div>

        {event.projectNumber && (
          <div className="text-sm text-gray-600">
            {t('lblProjectNumber', { ns: NS })}: {event.projectNumber}
          </div>
        )}
      </div>
    </U2000SaveCancelDialog>
  );
};

export default U3301EventDialog;
